import { OwnerCellData } from '@/components/tables/OwnerCell';
import { NoRowsOverlay } from '@/components/table/DataGridComponents/NoRowsOverlay';
import DataGridColumnMenu from '@/components/table/DataGridComponents/DataGridColumnMenu';
import DataGridColumnReorderToolbar from '@/components/table/DataGridComponents/DataGridColumnReorderToolbar';
import { formatDate } from '@/utils/constants';
import { Box, IconButton, Switch, Typography, useTheme } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { DataGrid, gridClasses } from '@mui/x-data-grid';
import React, { useMemo } from 'react';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { hasAutomationConfigDrawer } from '@/components/Drawers/AutomationConfigDrawer';
import {
	ColumnControlContextProvider,
	useDataGridColumnProps,
} from '@/context/columnControlContext';

const AutomationsTable = (props) => (
	<ColumnControlContextProvider variant='automations' value='list'>
		<AutomationsTableContent {...props} />
	</ColumnControlContextProvider>
);

const AutomationsTableContent = ({
	automations = [],
	updatingIds = [],
	onBrokerageToggle,
	onEditAutomation,
}) => {
	const theme = useTheme();

	const { getFeatureFlags } = useFeatureFlagContext();
	const canEditAutomations = Boolean(getFeatureFlags('editAutomations')?.active);

	const columns = useMemo(
		() =>
			[
				{
					field: 'title',
					headerName: 'Name',
					flex: 1.2,
					minWidth: 180,
					sortable: true,
					display: 'flex',
					hideable: false,
					renderCell: (params) => (
						<Typography>{params.row.title || params.row.name || '—'}</Typography>
					),
				},
				{
					field: 'description',
					headerName: 'Description',
					flex: 2.2,
					minWidth: 260,
					sortable: false,
					display: 'flex',
					renderCell: (params) => (
						<Typography>{params.row.description || '—'}</Typography>
					),
				},
				{
					field: 'lastModified',
					headerName: 'Last modified',
					flex: 1,
					minWidth: 170,
					sortable: true,
					display: 'flex',
					renderCell: (params) => (
						<Typography>
							{params.row.lastModified
								? formatDate(params.row.lastModified, true)
								: '—'}
						</Typography>
					),
				},
				{
					field: 'lastModifiedBy',
					headerName: 'Modified by',
					flex: 1,
					minWidth: 160,
					sortable: false,
					display: 'flex',
					renderCell: ({ row }) =>
						row.lastModifiedBy ? (
						// lastModifiedBy is the modifier's Firestore user doc id.
							<OwnerCellData
								userId={row.lastModifiedBy}
								color='text.primary'
							/>
						) : (
							<Typography>{'—'}</Typography>
						),
				},
				{
					field: 'enabled',
					headerName: 'Status',
					width: 110,
					sortable: true,
					align: 'left',
					headerAlign: 'left',
					display: 'flex',
					hideable: false,
					reorderable: false,
					renderCell: (params) => (
						<Switch
							size='small'
							checked={Boolean(params.row.enabled)}
							disabled={!canEditAutomations || updatingIds.includes(params.row.id)}
							onClick={(e) => e.stopPropagation()}
							onChange={(e) => {
								e.stopPropagation();
								onBrokerageToggle(params.row.id, e.target.checked);
							}}
							slotProps={{
								input: { 'aria-label': `${params.row.title} status` },
							}}
						/>
					),
				},
				{
					field: 'actions',
					headerName: '',
					width: 56,
					sortable: false,
					display: 'flex',
					hideable: false,
					reorderable: false,
					renderCell: (params) => {
						const canEdit =
							params.row?.editable && hasAutomationConfigDrawer(params.row?.name);
						if (!canEditAutomations || !canEdit) return null;
						return (
							<IconButton
								size='small'
								aria-label={`Edit ${params.row.title}`}
								color='primary'
								onClick={(e) => {
									e.stopPropagation();
									onEditAutomation?.(params.row);
								}}
							>
								<EditOutlinedIcon fontSize='small' />
							</IconButton>
						);
					},
				},
			].map((column) => ({ ...column, disableColumnMenu: column.hideable === false })),
		[canEditAutomations, updatingIds, onBrokerageToggle, onEditAutomation],
	);

	const {
		columns: orderedColumns,
		columnVisibilityModel,
		onColumnVisibilityModelChange,
		onColumnWidthChange,
	} = useDataGridColumnProps(columns);

	return (
		<Box
			position='relative'
			height='75vh'
			sx={{
				border: `1px solid ${theme.palette.divider}`,
				borderRadius: '8px',
				backgroundColor: theme.palette.background.paper,
			}}
		>
			<DataGrid
				rows={automations}
				columns={orderedColumns}
				columnVisibilityModel={columnVisibilityModel}
				onColumnVisibilityModelChange={onColumnVisibilityModelChange}
				onColumnWidthChange={onColumnWidthChange}
				width='100%'
				getRowHeight={() => 'auto'}
				disableRowSelectionOnClick
				disableColumnFilter
				hideFooter
				onRowClick={(params) => {
					const canEdit =
						params.row?.editable && hasAutomationConfigDrawer(params.row?.name);
					if (canEditAutomations && canEdit) {
						onEditAutomation?.(params.row);
					}
				}}
				slots={{
					noRowsOverlay: () => <NoRowsOverlay objectType='automations' />,
					toolbar: DataGridColumnReorderToolbar,
					columnMenu: DataGridColumnMenu,
				}}
				sx={{
					'& .MuiDataGrid-editInputCell': {
						backgroundColor: 'transparent',
					},
					'--DataGrid-overlayHeight': '3em',
					'& .MuiDataGrid-columnHeaderTitle': {
						fontWeight: 600,
						color: theme.palette.text.primary,
					},
					'& .MuiDataGrid-cell': {
						padding: 1,
						minHeight: '42px',
					},
					[`& .${gridClasses.row}`]: {
						minHeight: '52px !important',
						cursor: 'default',
					},
					border: 'none',
					[`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
						outline: 'none',
					},
					[`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
						{
							outline: 'none',
						},
					[`& .${gridClasses.columnHeader}`]: {
						borderRight: 'none !important',
					},
					borderRadius: '8px',
				}}
			/>
		</Box>
	);
};

export default AutomationsTable;
